/* eslint-disable max-lines */
import { useCallback, useMemo, useRef, useState } from "react";

import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { cn } from "@design-lab/shared/lib/tailwindcss";
import { hslToHex } from "@design-lab/widgets/artboard/lib/colorUtils";
import { useDragHandler } from "@design-lab/widgets/artboard/model";

type ColorGradientPickerProps = {
  hue: number;
  saturation: number;
  lightness: number;
  currentColor: string;
  onColorChange: (hue: number, saturation: number, lightness: number) => void;
};

type HSL = { h: number; s: number; l: number };

const HUE_GRADIENT =
  "#ff0000, #ff8000, #ffff00, #80ff00, #00ff00, #00ff80, #00ffff, #0080ff, #0000ff, #8000ff, #ff00ff, #ff0080, #ff0000";

const LIGHTNESS_GRADIENT = `
  rgba(255, 255, 255, 1), 
  rgba(255, 255, 255, 0) 50%,
  rgba(0, 0, 0, 0) 50%,
  rgba(0, 0, 0, 1)
`;

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

export function ColorGradientPicker({
  hue,
  saturation,
  lightness,
  currentColor,
  onColorChange
}: ColorGradientPickerProps) {
  const gradientRef = useRef<HTMLDivElement>(null);
  const hueSliderRef = useRef<HTMLDivElement>(null);
  const { isDesktop } = useResponsive();

  const [isDragging, setIsDragging] = useState(false);
  const [dragState, setDragState] = useState<HSL>({ h: hue, s: saturation, l: lightness });

  const displayHSL = useMemo(
    () => (isDragging ? dragState : { h: hue, s: saturation, l: lightness }),
    [isDragging, dragState, hue, saturation, lightness]
  );

  const displayColor = useMemo(
    () => (isDragging ? hslToHex(dragState.h, dragState.s, dragState.l) : currentColor),
    [isDragging, dragState.h, dragState.s, dragState.l, currentColor]
  );

  const handleDragStart = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    setDragState(currentState => {
      onColorChange(currentState.h, currentState.s, currentState.l);
      return currentState;
    });
  }, [onColorChange]);

  const { handleMouseDown: handleGradientMouseDown, handleTouchStart: handleGradientTouchStart } =
    useDragHandler({
      elementRef: gradientRef,
      onUpdate: useCallback(
        (rect, coords) => {
          handleDragStart();
          const x = clamp(coords.clientX - rect.left, 0, rect.width);
          const y = clamp(coords.clientY - rect.top, 0, rect.height);
          const s = Math.round(clamp((x / rect.width) * 100, 0, 100));
          const l = Math.round(clamp(100 - (y / rect.height) * 100, 0, 100));
          setDragState({ h: hue, s, l });
        },
        [hue, handleDragStart]
      ),
      onEnd: handleDragEnd
    });

  const { handleMouseDown: handleHueSliderMouseDown, handleTouchStart: handleHueSliderTouchStart } =
    useDragHandler({
      elementRef: hueSliderRef,
      onUpdate: useCallback(
        (rect, coords) => {
          handleDragStart();
          const x = clamp(coords.clientX - rect.left, 0, rect.width);
          const h = Math.round(clamp((x / rect.width) * 360, 0, 359));
          setDragState({ h, s: saturation, l: lightness });
        },
        [saturation, lightness, handleDragStart]
      ),
      onEnd: handleDragEnd
    });

  return (
    <div className={cn("flex flex-1 flex-col", isDesktop ? "mb-3" : "mb-4")}>
      <div
        className={cn(
          "relative w-full cursor-crosshair overflow-hidden rounded-lg",
          isDesktop ? "mb-3 min-h-0 flex-1" : "mb-4 h-48"
        )}
        ref={gradientRef}
        onMouseDown={handleGradientMouseDown}
        onTouchStart={handleGradientTouchStart}
      >
        <div
          className="h-full w-full"
          style={{
            background: `linear-gradient(to right, hsl(${displayHSL.h}, 0%, 50%), hsl(${displayHSL.h}, 100%, 50%))`
          }}
        >
          <div
            className="h-full w-full"
            style={{
              background: `linear-gradient(to bottom, ${LIGHTNESS_GRADIENT})`
            }}
          >
            <div
              className="pointer-events-none absolute size-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-lg"
              style={{
                left: `${displayHSL.s}%`,
                top: `${100 - displayHSL.l}%`,
                backgroundColor: displayColor
              }}
            />
          </div>
        </div>
      </div>

      <div
        className={cn(
          "relative h-4 w-full cursor-pointer overflow-hidden rounded-full",
          isDesktop ? "mb-3" : "mb-4"
        )}
        ref={hueSliderRef}
        onMouseDown={handleHueSliderMouseDown}
        onTouchStart={handleHueSliderTouchStart}
      >
        <div
          className="h-full w-full"
          style={{
            background: `linear-gradient(to right, ${HUE_GRADIENT})`
          }}
        />
        <div
          className="pointer-events-none absolute top-1/2 size-6 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-lg"
          style={{
            left: `${(displayHSL.h / 360) * 100}%`,
            backgroundColor: `hsl(${displayHSL.h}, 100%, 50%)`
          }}
        />
      </div>
    </div>
  );
}
